import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Separator,
} from '@databro/ui';

export default function Settings() {
  const queryClient = useQueryClient();
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');

  const { data: apiKeys, isLoading } = useQuery({
    queryKey: ['apiKeys'],
    queryFn: () => api.getApiKeys(),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => api.createApiKey(name),
    onSuccess: (data) => {
      setGeneratedKey(data.key);
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteApiKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
    },
  });

  if (isLoading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your API keys and account settings</p>
      </div>

      {/* API Keys */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>API Keys</CardTitle>
            <CardDescription>Manage keys for accessing the API</CardDescription>
          </div>
          <Button
            onClick={() => {
              setNewKeyName('');
              setGeneratedKey('');
              setShowNewKeyModal(true);
            }}
          >
            Create New Key
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {apiKeys?.map((key, index) => (
            <div key={key.id}>
              {index > 0 && <Separator />}
              <div className="px-6 py-4 flex items-center justify-between">
                <div>
                  <div className="font-medium">{key.name}</div>
                  <div className="text-sm text-muted-foreground font-mono">{key.keyPrefix}...</div>
                  <div className="text-xs text-muted-foreground">
                    Created {new Date(key.createdAt).toLocaleDateString()}
                    {key.lastUsedAt &&
                      ` · Last used ${new Date(key.lastUsedAt).toLocaleDateString()}`}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    if (confirm('Are you sure you want to revoke this API key?')) {
                      deleteMutation.mutate(key.id);
                    }
                  }}
                  disabled={deleteMutation.isPending}
                >
                  Revoke
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* New Key Modal */}
      <Dialog open={showNewKeyModal} onOpenChange={setShowNewKeyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {generatedKey ? 'API Key Created' : 'Create New API Key'}
            </DialogTitle>
          </DialogHeader>

          {generatedKey ? (
            <div className="space-y-4">
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                <p className="text-sm font-medium mb-2">
                  Save this key securely. You won't be able to see it again.
                </p>
                <code className="block bg-background p-3 rounded border text-sm break-all font-mono">
                  {generatedKey}
                </code>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigator.clipboard.writeText(generatedKey)}
              >
                Copy to Clipboard
              </Button>
              <Button className="w-full" onClick={() => setShowNewKeyModal(false)}>
                Done
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="keyName">Key Name</Label>
                <Input
                  id="keyName"
                  value={newKeyName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Production, Development"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNewKeyModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => createMutation.mutate(newKeyName || 'Unnamed')}
                  disabled={createMutation.isPending}
                >
                  Create
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
